"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import { useUser } from "@clerk/nextjs";

export default function Home() {
  const { isSignedIn } = useUser();
  
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative bg-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl md:text-6xl">
                <span className="block">Deepen your</span>
                <span className="block text-indigo-600">Biblical understanding</span>
              </h1>
              <p className="mt-6 text-xl text-gray-500 max-w-3xl">
                Organize your Bible study with our intuitive tools. Save verses, create topic groups, and add your personal insights as you read.
              </p>
              <div className="mt-10 flex gap-4">
                {!isSignedIn ? (
                  <Link href="/sign-in">
                    <button className="bg-indigo-600 text-white px-6 py-3 rounded-md text-lg font-medium shadow-md hover:bg-indigo-700">
                      Get Started
                    </button>
                  </Link>
                ) : (
                  <Link href="/user/Bible">
                    <button className="bg-indigo-600 text-white px-6 py-3 rounded-md text-lg font-medium shadow-md hover:bg-indigo-700">
                      Open Bible Study
                    </button>
                  </Link>
                )}
                <Link href="#features">
                  <button className="bg-white text-indigo-600 border border-indigo-600 px-6 py-3 rounded-md text-lg font-medium hover:bg-indigo-50">
                    Learn More
                  </button>
                </Link>
              </div>
            </div>
            <div className="relative h-64 md:h-96 rounded-lg overflow-hidden shadow-xl">
              <Image 
                src="/bible-app-preview.jpg" 
                alt="Bible App Preview" 
                fill
                style={{objectFit: "cover"}}
                priority
              />
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Features designed for serious Bible study
            </h2>
            <p className="mt-4 text-xl text-gray-500">
              Everything you need to organize your thoughts and insights
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Feature 1 */}
            <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Easy Navigation</h3>
              <p className="text-gray-600">
                Quickly navigate between books, chapters, and verses with our intuitive interface. Remember your last position with automatic bookmarks.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6Z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Topic Organization</h3>
              <p className="text-gray-600">
                Create custom topics and save relevant verses to them. Verses are automatically grouped by book and chapter for clear organization.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Personal Notes</h3>
              <p className="text-gray-600">
                Add your own insights to verses as you study. Your notes stay with the verses, making it easy to build your understanding over time.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="py-24 bg-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 text-xl font-bold mx-auto mb-4">1</div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Create Topics</h3>
              <p className="text-gray-600">Start by setting up study topics or questions you want to explore</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 text-xl font-bold mx-auto mb-4">2</div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Study Scripture</h3>
              <p className="text-gray-600">Navigate through books and chapters to read the Bible</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 text-xl font-bold mx-auto mb-4">3</div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Save Verses</h3>
              <p className="text-gray-600">Press 's' to save important verses to your topics with notes</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 text-xl font-bold mx-auto mb-4">4</div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Review Insights</h3>
              <p className="text-gray-600">Reference your organized study notes anytime</p>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-indigo-700">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between">
          <div className="text-center md:text-left mb-8 md:mb-0">
            <h2 className="text-2xl font-extrabold text-white tracking-tight sm:text-3xl">
              Ready to start your Bible study journey?
            </h2>
            <p className="mt-3 max-w-3xl text-lg text-indigo-200">
              Sign up and begin organizing your Bible study today.
            </p>
          </div>
          <div>
            {!isSignedIn ? (
              <Link href="/sign-in">
                <button className="bg-white text-indigo-600 px-8 py-3 rounded-md text-base font-medium shadow-lg hover:bg-gray-100">
                  Sign Up Now
                </button>
              </Link>
            ) : (
              <Link href="/user/Bible">
                <button className="bg-white text-indigo-600 px-8 py-3 rounded-md text-base font-medium shadow-lg hover:bg-gray-100">
                  Go to Bible Study
                </button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
